import {
  delayLabel,
  estimateFlight,
  type FlightEnds,
  type LiveTimes,
  type PhoneLink,
} from './flight-estimate';

/**
 * A flight's status the way byAir and TripIt show it: one word or two, a colour, the times to
 * trust (with the schedule to strike through when they've moved), gate changes, where the
 * incoming plane is, and whether a connection still works.
 */

const MIN = 60_000;

export type StatusTone = 'ok' | 'late' | 'bad' | 'muted';

export interface LiveExtras extends LiveTimes {
  diverted?: boolean;
  gateOrigin?: string | null;
  gateDestination?: string | null;
  gateOriginWas?: string | null;
  gateDestinationWas?: string | null;
  baggageClaim?: string | null;
  inbound?: {
    origin: string | null;
    estimatedIn: number | null;
    delayMinutes: number | null;
    status: string | null;
    atGate?: boolean;
  } | null;
}

export interface TimePair {
  scheduled: number;
  /** Best time we have: actual, else estimated, else scheduled. */
  best: number;
  actual: boolean;
  /** Moved by five minutes or more. */
  moved: boolean;
}

export interface FlightStatus {
  label: string;
  tone: StatusTone;
  departDelay: number;
  arriveDelay: number;
  depart: TimePair;
  arrive: TimePair;
  /** "Gate changed from B12" and the like. */
  notices: string[];
  /** Live data is in and recent. */
  live: boolean;
}

function tone(delay: number): StatusTone {
  if (delay >= 60) return 'bad';
  if (delay >= 15) return 'late';
  return 'ok';
}

export function flightStatus(
  plan: { startAt: number; endAt: number | null; cancelled?: boolean },
  live: LiveExtras | null | undefined,
  now: number,
  ends?: FlightEnds,
  link?: PhoneLink,
): FlightStatus {
  const l = live ?? {};
  const e = estimateFlight(plan, l, now, ends, link);
  const schedOut = l.scheduledOut ?? plan.startAt;
  const schedIn = l.scheduledIn ?? plan.endAt ?? e.in;
  const depart: TimePair = {
    scheduled: schedOut,
    best: e.out,
    actual: l.actualOut != null,
    moved: Math.abs(e.out - schedOut) >= 5 * MIN,
  };
  const arrive: TimePair = {
    scheduled: schedIn,
    best: e.in,
    actual: l.actualIn != null,
    moved: Math.abs(e.in - schedIn) >= 5 * MIN,
  };

  let label: string;
  let t: StatusTone;
  if (e.phase === 'cancelled') {
    label = 'Cancelled';
    t = 'bad';
  } else if (l.diverted) {
    label = 'Diverted';
    t = 'bad';
  } else if (e.phase === 'arrived' || e.phase === 'landed') {
    label = e.arriveDelay >= 15 ? `Landed ${delayLabel(e.arriveDelay)}` : 'Landed';
    t = e.phase === 'arrived' ? 'muted' : tone(e.arriveDelay);
  } else if (e.phase === 'air' || e.phase === 'taxiing') {
    label = e.arriveDelay >= 15 ? `In the air, ${delayLabel(e.arriveDelay)}` : 'In the air';
    t = tone(e.arriveDelay);
  } else if (e.departDelay >= 5) {
    label = `Delayed ${delayLabel(e.departDelay).replace(' late', '')}`;
    t = tone(e.departDelay);
  } else if (e.departDelay <= -5) {
    label = delayLabel(e.departDelay);
    t = 'ok';
  } else {
    label = e.phase === 'boarding' ? 'Boarding soon' : 'On time';
    t = 'ok';
  }

  const notices: string[] = [];
  if (l.gateOriginWas && l.gateOrigin && l.gateOriginWas !== l.gateOrigin)
    notices.push(`Gate changed to ${l.gateOrigin} (was ${l.gateOriginWas})`);
  if (l.gateDestinationWas && l.gateDestination && l.gateDestinationWas !== l.gateDestination)
    notices.push(`Arrival gate changed to ${l.gateDestination}`);
  const inbound = l.inbound;
  // Where the plane is has its own card; this only warns when it can't make the departure.
  if (
    inbound &&
    !inbound.atGate &&
    e.phase !== 'air' &&
    e.phase !== 'landed' &&
    e.phase !== 'arrived' &&
    inbound.estimatedIn != null &&
    inbound.estimatedIn > e.out - 20 * MIN
  )
    notices.push(
      `Your plane${inbound.origin ? ` from ${inbound.origin}` : ''} gets in late, so this may leave late too`,
    );

  return {
    label,
    tone: t,
    departDelay: e.departDelay,
    arriveDelay: e.arriveDelay,
    depart,
    arrive,
    notices,
    live: !e.offline,
  };
}

export interface Connection {
  minutes: number;
  /** Under the usual minimum for the airport, or already gone. */
  risk: 'ok' | 'tight' | 'missed';
  label: string;
}

/**
 * Time to make the next flight out of the airport this one lands at. Tight under 45 minutes
 * (60 when crossing a border), missed once arrival is after the next departure.
 */
export function connection(
  arriveAt: number,
  nextDepartAt: number,
  international: boolean,
): Connection {
  const minutes = Math.round((nextDepartAt - arriveAt) / MIN);
  const min = international ? 60 : 45;
  if (minutes < 10)
    return { minutes, risk: 'missed', label: 'Connection at risk: lands after boarding closes' };
  if (minutes < min) return { minutes, risk: 'tight', label: `Tight connection: ${minutes} min` };
  return { minutes, risk: 'ok', label: `${minutes} min to connect` };
}
